import { useEffect } from 'react';
import { useTranslation } from '@/i18n/translations';
import { useLocation } from 'react-router-dom';

const SEOHead = () => {
  const { lang } = useTranslation();
  const location = useLocation();

  const seoData = {
    en: {
      title: 'AI Receptionist for Wellness — Automated Bookings | Chatity',
      description: "Smart AI chatbot that automates client bookings for your wellness business. Speaks your clients' language. Send bookings to Email or Telegram. No weekends or lunch breaks needed.",
      keywords: 'AI chatbot, automated bookings, appointment booking, multilingual chatbot, wellness automation, booking widget, AI assistant, client scheduling, chatity',
      ogTitle: 'AI Receptionist for Wellness | Chatity',
      twitterTitle: 'AI Receptionist for Wellness | Chatity'
    },
    ru: {
      title: 'AI-администратор для wellness — Автоматическая запись | Chatity',
      description: 'Умный AI чат-бот, который автоматизирует запись клиентов для вашего wellness-бизнеса. Отвечает на языке клиента. Отправка заявок в Email или Telegram. Без выходных и обедов.',
      keywords: 'AI чат-бот, автоматическая запись, запись на прием, многоязычный чатбот, wellness автоматизация, виджет записи, AI ассистент, планирование клиентов, chatity',
      ogTitle: 'AI-администратор для wellness | Chatity',
      twitterTitle: 'AI-администратор для wellness | Chatity'
    },
    fr: {
      title: 'Réceptionniste IA pour le Wellness — Réservations Automatiques | Chatity',
      description: 'Chatbot IA intelligent qui automatise les réservations clients pour votre activité wellness. Parle la langue de vos clients. Envoi vers Email ou Telegram. Sans week-ends ni pauses déjeuner.',
      keywords: 'chatbot IA, réservations automatiques, prise de rendez-vous, chatbot multilingue, automatisation wellness, widget de réservation, assistant IA, planification clients, chatity',
      ogTitle: 'Réceptionniste IA pour le Wellness | Chatity',
      twitterTitle: 'Réceptionniste IA pour le Wellness | Chatity'
    }
  };

  useEffect(() => {
    // Don't override SEO on blog pages - they use React Helmet
    if (location.pathname.startsWith('/blog')) {
      return;
    }

    const currentLang = lang || 'en';
    const data = seoData[currentLang] || seoData.en;

    // Update title
    document.title = data.title;

    // Update meta tags
    const updateMetaTag = (name, content, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      
      meta.setAttribute('content', content);
    };

    // Update basic meta tags
    updateMetaTag('title', data.title);
    updateMetaTag('description', data.description);
    updateMetaTag('keywords', data.keywords);

    // Update Open Graph tags
    updateMetaTag('og:title', data.ogTitle, true);
    updateMetaTag('og:description', data.description, true);
    updateMetaTag('og:locale', currentLang === 'ru' ? 'ru_RU' : currentLang === 'fr' ? 'fr_FR' : 'en_US', true);

    // Update Twitter tags (full set — card type + image + url)
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', data.twitterTitle);
    updateMetaTag('twitter:description', data.description);
    updateMetaTag('twitter:image', 'https://chatity.io/og-default.png');
    updateMetaTag('twitter:url', 'https://chatity.io');

    // Open Graph image (was missing — required for proper FB/LinkedIn previews)
    updateMetaTag('og:image', 'https://chatity.io/og-default.png', true);
    updateMetaTag('og:url', 'https://chatity.io', true);
    updateMetaTag('og:type', 'website', true);

    // Update html lang attribute
    document.documentElement.lang = currentLang;

    // Add hreflang tags for home page language versions
    // Remove existing hreflang tags
    const existingHreflang = document.querySelectorAll('link[rel="alternate"][hreflang]');
    existingHreflang.forEach(tag => tag.remove());

    // Add hreflang tags for each language
    const languages = [
      { code: 'en', url: 'https://chatity.io?lang=en' },
      { code: 'fr', url: 'https://chatity.io?lang=fr' },
      { code: 'ru', url: 'https://chatity.io?lang=ru' }
    ];

    languages.forEach(({ code, url }) => {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = code;
      link.href = url;
      document.head.appendChild(link);
    });

    // Add x-default
    const defaultLink = document.createElement('link');
    defaultLink.rel = 'alternate';
    defaultLink.hreflang = 'x-default';
    defaultLink.href = 'https://chatity.io';
    document.head.appendChild(defaultLink);

  }, [lang, location.pathname]);

  return null; // This component doesn't render anything
};

export default SEOHead;
